
import React, { useEffect, useState } from "react";

function Preloader({ onComplete }) {
    const [fading, setFading] = useState(false);

    useEffect(() => {
        // Start fading out the preloader after the animation completes
        const fadeTimer = setTimeout(() => {
            setFading(true);
        }, 2000);

        // Call onComplete fully after the fade completes
        const removeTimer = setTimeout(() => {
            onComplete();
        }, 2600);

        return () => {
            clearTimeout(fadeTimer);
            clearTimeout(removeTimer);
        };
    }, [onComplete]);

    return (
        <div
            className={`fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#050505] transition-opacity duration-1000 ${fading ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        >
            {/* Background ambient glow */}
            <div className="absolute w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-[120px] animate-pulse" />



            <div className="relative w-48 h-48 md:w-64 md:h-64 flex items-center justify-center">
                <svg viewBox="-100 -100 200 200" className="w-full h-full overflow-visible">
                    <defs>
                        <linearGradient id="goldGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" style={{ stopColor: "#FFD700", stopOpacity: 1 }} />
                            <stop offset="100%" style={{ stopColor: "#B8860B", stopOpacity: 1 }} />
                        </linearGradient>
                        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="3" result="blur" />
                            <feComposite in="SourceGraphic" in2="blur" operator="over" />
                        </filter>
                    </defs>

                    {/* Outer Petals (12) */}
                    {[...Array(12)].map((_, i) => (
                        <g key={`outer-${i}`} transform={`rotate(${i * 30})`}>
                            <path
                                d="M 0 0 C -15 -20 -15 -50 0 -70 C 15 -50 15 -20 0 0 Z"
                                fill="url(#goldGradient)"
                                style={{
                                    animation: `bloomScale 1.5s cubic-bezier(0.2, 0, 0.2, 1) ${i * 0.03}s forwards`,
                                    transform: 'scale(0)',
                                    filter: 'url(#glow)',
                                    opacity: 0.7
                                }}
                            />
                        </g>
                    ))}

                    {/* Middle Petals (8) */}
                    {[...Array(8)].map((_, i) => (
                        <g key={`mid-${i}`} transform={`rotate(${i * 45 + 22.5})`}>
                            <path
                                d="M 0 0 C -12 -15 -12 -40 0 -55 C 12 -40 12 -15 0 0 Z"
                                fill="#FFB300"
                                style={{
                                    animation: `bloomScale 1.3s cubic-bezier(0.2, 0, 0.2, 1) ${0.3 + i * 0.05}s forwards`,
                                    transform: 'scale(0)',
                                    filter: 'url(#glow)',
                                    opacity: 0.85
                                }}
                            />
                        </g>
                    ))}

                    {/* Inner Petals (6) */}
                    {[...Array(6)].map((_, i) => (
                        <g key={`inner-${i}`} transform={`rotate(${i * 60})`}>
                            <path
                                d="M 0 0 C -8 -10 -8 -30 0 -45 C 8 -30 8 -10 0 0 Z"
                                fill="#FFF"
                                style={{
                                    animation: `bloomScale 1.1s cubic-bezier(0.2, 0, 0.2, 1) ${0.6 + i * 0.06}s forwards`,
                                    transform: 'scale(0)',
                                    filter: 'url(#glow)',
                                    opacity: 1
                                }}
                            />
                        </g>
                    ))}

                    {/* Center Core */}
                    <circle cx="0" cy="0" r="5" fill="#FFF" style={{
                        animation: 'bloomScale 1.2s ease-out 1.5s forwards',
                        transform: 'scale(0)',
                        filter: 'url(#glow)'
                    }} />
                </svg>
            </div>

            <div className="mt-12 overflow-hidden">
                <div className="flex text-white text-3xl md:text-5xl font-extralight" role="presentation">
                    {['A', 'K', 'H', 'A', 'N', 'D', 'A', 'A'].map((letter, index) => (
                        <span
                            key={index}
                            className="inline-block"
                            style={{
                                opacity: 0,
                                animation: `letterPopUp 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) ${0.8 + index * 0.05}s forwards`,
                                textShadow: '0 0 20px rgba(212, 175, 55, 0.3)',
                                marginRight: index === 7 ? '0' : '0.5em'
                            }}
                        >
                            {letter}
                        </span>
                    ))}
                </div>
            </div>

            <style>{`
                @keyframes bloomScale {
                    0% {
                        transform: scale(0) translateY(10px);
                        opacity: 0;
                    }
                    20% {
                        opacity: 1;
                    }
                    100% {
                        transform: scale(1) translateY(0);
                        opacity: 1;
                    }
                }
                @keyframes letterPopUp {
                    0% {
                        opacity: 0;
                        transform: translateY(100%);
                    }
                    100% {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
}

export default Preloader;
